import { useEffect, useMemo } from 'react';

import { useGetChatsQuery } from '../../services/chatAdministrationApi';
import { fleetGroupFilterSelectionChanged, useSelectedFleetGroupTagIds } from './fleetGroupFilterSlice';
import {
    filterChatsByFleetGroups,
    getValidSelectedFleetGroupTagIds,
    matchesSelectedFleetGroups,
} from './fleetGroupFilter';
import type { Channel } from '../chat/chatSlice';
import { useAppDispatch } from '../../configuration/setup/hooks';

export const useFilteredChats = () => {
    const dispatch = useAppDispatch();
    const { data: chats = [], isLoading, isSuccess } = useGetChatsQuery();
    const storedSelectedTagIds = useSelectedFleetGroupTagIds();
    const validSelectedTagIds = useMemo(
        () => getValidSelectedFleetGroupTagIds(chats, storedSelectedTagIds),
        [chats, storedSelectedTagIds]
    );

    useEffect(() => {
        if (!isSuccess || storedSelectedTagIds.length === validSelectedTagIds.length) {
            return;
        }

        dispatch(fleetGroupFilterSelectionChanged(validSelectedTagIds));
    }, [dispatch, isSuccess, storedSelectedTagIds, validSelectedTagIds]);

    const filteredChats = useMemo(
        () => filterChatsByFleetGroups(chats, validSelectedTagIds),
        [chats, validSelectedTagIds]
    );

    return { chats, filteredChats, validSelectedTagIds, isLoading };
};

export const useFilteredChannels = (channels: Channel[]) => {
    const { chats, validSelectedTagIds } = useFilteredChats();

    return useMemo(() => {
        const chatsById = new Map(chats.map(chat => [chat.id, chat]));
        return channels.filter(channel =>
            matchesSelectedFleetGroups(chatsById.get(channel.channelId), validSelectedTagIds)
        );
    }, [channels, chats, validSelectedTagIds]);
};

export const useFilteredTotalUnreadMessageCount = (channels: Channel[]) => {
    const filteredChannels = useFilteredChannels(channels);

    return useMemo(
        () => filteredChannels.reduce((total, channel) => total + channel.unreadMessageCount, 0),
        [filteredChannels]
    );
};
